import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Delete, Download, Restore } from "@mui/icons-material";

/**
 * Dialog for managing backup (restore, download, delete)
 */
const ManageBackupDialog = ({
  open,
  onClose,
  backup,
  onRestore,
  onDelete,
  onDownload,
  restoreLoading,
  deleteLoading,
}) => {
  const [tab, setTab] = useState("restore");
  const [downloadOptions, setDownloadOptions] = useState({
    files: true,
    database: true,
    metadata: false,
  });
  const [restoreOptions, setRestoreOptions] = useState({
    restoreFiles: true,
    restoreDatabase: true,
  });

  // Reset state when backup changes
  useEffect(() => {
    if (backup) {
      setTab("restore");
      setDownloadOptions({ files: true, database: true, metadata: false });
      setRestoreOptions({ restoreFiles: true, restoreDatabase: true });
    }
  }, [backup]);

  if (!backup) return null;

  const handleDownloadOptionChange = (event) => {
    setDownloadOptions({
      ...downloadOptions,
      [event.target.name]: event.target.checked,
    });
  };

  const handleDownloadClick = () => {
    onDownload(downloadOptions);
    onClose();
  };

  const handleRestoreClick = () => {
    onRestore(restoreOptions);
    onClose();
  };

  const handleDeleteClick = () => {
    onDelete();
    onClose();
  };

  const isDownloadDisabled =
    !downloadOptions.files &&
    !downloadOptions.database &&
    !downloadOptions.metadata;

  const isRestoreDisabled =
    !restoreOptions.restoreFiles && !restoreOptions.restoreDatabase;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Backup</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          <strong>{backup.backupName}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Created: {new Date(backup.createdAt).toLocaleString()}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
            <Tab label="Restore" value="restore" />
            <Tab label="Download" value="download" />
            <Tab label="Delete" value="delete" />
          </Tabs>
        </Box>

        {/* Restore Panel */}
        {tab === "restore" && (
          <Box sx={{ pt: 3 }}>
            <Typography gutterBottom>
              Select what to restore from this backup:
            </Typography>
            <FormGroup sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={restoreOptions.restoreFiles}
                    onChange={(e) =>
                      setRestoreOptions({
                        ...restoreOptions,
                        restoreFiles: e.target.checked,
                      })
                    }
                  />
                }
                label="Restore Files (WordPress files, themes, plugins)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={restoreOptions.restoreDatabase}
                    onChange={(e) =>
                      setRestoreOptions({
                        ...restoreOptions,
                        restoreDatabase: e.target.checked,
                      })
                    }
                  />
                }
                label="Restore Database (posts, pages, settings)"
              />
            </FormGroup>
            {isRestoreDisabled && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Select at least one component to restore.
              </Alert>
            )}
            <Alert severity="warning" sx={{ mt: 2 }}>
              A pre-restore backup will be automatically created. Changes made
              after this backup will be lost for the selected components.
            </Alert>
          </Box>
        )}

        {/* Download Panel */}
        {tab === "download" && (
          <Box sx={{ pt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Components
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={downloadOptions.files}
                    onChange={handleDownloadOptionChange}
                    name="files"
                  />
                }
                label={`Files (${backup.sizeMB || "N/A"} MB)`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={downloadOptions.database}
                    onChange={handleDownloadOptionChange}
                    name="database"
                  />
                }
                label="Database"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={downloadOptions.metadata}
                    onChange={handleDownloadOptionChange}
                    name="metadata"
                  />
                }
                label="Metadata (.json)"
              />
            </FormGroup>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Each selected component will be downloaded as a separate file.
            </Typography>
          </Box>
        )}

        {/* Delete Panel */}
        {tab === "delete" && (
          <Box sx={{ pt: 3 }}>
            <Alert severity="error">
              This action is permanent and cannot be undone. Are you sure you
              want to delete this backup?
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>

        {tab === "restore" && (
          <Button
            onClick={handleRestoreClick}
            variant="contained"
            color="primary"
            disabled={restoreLoading || isRestoreDisabled}
            startIcon={
              restoreLoading ? <CircularProgress size={16} /> : <Restore />
            }
          >
            Restore
          </Button>
        )}

        {tab === "download" && (
          <Button
            onClick={handleDownloadClick}
            variant="contained"
            color="primary"
            disabled={isDownloadDisabled}
            startIcon={<Download />}
          >
            Download
          </Button>
        )}

        {tab === "delete" && (
          <Button
            onClick={handleDeleteClick}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? <CircularProgress size={16} /> : <Delete />
            }
          >
            Delete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageBackupDialog;
